import { Flex, Box } from '@chakra-ui/react';
import VAccordionCard from '@voly/ui-components/src/v2/VAccordionCard';
import { ReactNode } from 'react';
import { MdAdd } from 'react-icons/md';
import { isCustomFieldLockable } from 'src/app/project/modules/custom-fields/utils/is-custom-field-lockable';
import { isCustomFieldVisibilityEditable } from 'src/app/project/modules/custom-fields/utils/is-custom-field-visibility-editable';
import { TLayoutGroup } from '../organisms/CustomFieldGroupsSettings';
import VDotMenu from '@voly/ui-components/src/v2/VDotMenu';
import VButton from '@voly/ui-components/src/v2/VButton.jsx';

type TGroupMenuOption = {
  value: string;
  label: ReactNode;
  onClick: () => void;
  color?: 'destructive';
};

interface CustomFieldGroupProps {
  group: TLayoutGroup;
  content: ReactNode;
  dragHandle: ReactNode;
  isPreview: boolean;
  onAddField: () => void;
  onUngroup: () => void;
  onRename: () => void;
  onDeleteGroup: () => void;
  onSetGroupVisibility: (display: boolean) => void;
  onSetGroupLocked: (locked: boolean) => void;
}

const CustomFieldGroup = ({
  group,
  content,
  dragHandle,
  isPreview,
  onUngroup,
  onAddField,
  onRename,
  onDeleteGroup,
  onSetGroupVisibility,
  onSetGroupLocked,
}: CustomFieldGroupProps) => {
  const isEmpty = group.items.length === 0;
  const editableFields = group.items.filter(isCustomFieldVisibilityEditable);
  const shouldShowAll = editableFields.every((field) => !field.display);
  const lockableFields = group.items.filter(isCustomFieldLockable);
  const shouldUnlockAll = lockableFields.every((field) => field.lockedValue);

  const dropdownOptions: TGroupMenuOption[] = [
    {
      value: 'rename',
      label: 'Rename',
      onClick: onRename,
    },
  ];

  if (!isEmpty) {
    if (editableFields.length > 0) {
      dropdownOptions.push({
        value: 'visibility',
        label: shouldShowAll ? 'Show all' : 'Hide all',
        onClick: () => onSetGroupVisibility(shouldShowAll),
      });
    }

    if (lockableFields.length > 0) {
      dropdownOptions.push({
        value: 'lock',
        label: shouldUnlockAll ? 'Unlock all' : 'Lock all',
        onClick: () => onSetGroupLocked(!shouldUnlockAll),
      });
    }

    dropdownOptions.push({
      value: 'ungroup',
      label: 'Ungroup',
      onClick: onUngroup,
    });
  } else {
    dropdownOptions.push({
      value: 'delete',
      label: 'Delete Group',
      color: 'destructive',
      onClick: onDeleteGroup,
    });
  }

  return (
    <Flex gap={4} direction="column" my={4}>
      <VAccordionCard
        indicatorPosition={isPreview ? 'none' : 'start'}
        title={group.title}
        value={group.id ?? group.title}
        defaultOpen
        bodyPadding={false}
        truncateTitle
        prefixAction={dragHandle}
        suffixAction={
          <VDotMenu
            key={group.title}
            size="small"
            triggerLabel={`Options for ${group.title}`}
            items={dropdownOptions}
          />
        }
      >
        {content}
        {isEmpty ? null : (
          <Box data-testid="add-field-row" p={2}>
            <VButton prefixIcon={MdAdd} color="tertiary" onClick={onAddField}>
              Add field
            </VButton>
          </Box>
        )}
      </VAccordionCard>
    </Flex>
  );
};

export default CustomFieldGroup;
